import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { SpringValue } from 'react-spring'
import { useTrail } from 'react-spring'
import { useMoteRecorder } from '@mote/helpers/hooks/recorder'
import { RecordingTime } from '@mote/presentation'
import { RecordingError } from '@mote/recipes'
import {
  Button,
  Container,
  RecordButton,
  RecorderButtonRow,
  Spinner,
  Text
} from '@mote/ui'
import { TranscriptTranslationPicker } from '@mote/ui/components'
import { useViewportWidth } from '@xstyled/styled-components'
import { usePreloadedMoteConversationStateContext } from '../mote'
import { MeasuredAudioWaveformRecorder } from './AudioWaveformRecorder'
import { RecorderAvatar } from './RecorderAvatar'
import {
  AnimatedFeaturesWrapper,
  AnimatedStyledMainContentWrapper,
  AnimatedWrapper,
  StyledCTAContentWrapper,
  StyledClickToRecordText,
  StyledFeaturesWrapper,
  StyledIdleContentWrapper,
  StyledLanguagePickerWrapper,
  StyledPausedText,
  StyledRecorderButtonsWrapper,
  StyledRecorderControlsWrapper,
  StyledRecordingContentWrapper,
  StyledRecordingMainContentWrapper,
  StyledScrubberWrapper,
  StyledThinkingContentWrapper
} from './WebRecorder.styles'
import { WebRecorderFeaturesBanner } from './WebRecorderFeaturesBanner'
import { useTranscriptionLocale } from './useTranscriptionLocale'
import { useWebRecorderAnimation } from './useWebRecorderAnimation'

export type WebRecorderProps =
  | { isAnonymous: true }
  | {
      isAnonymous: false
      name: string
      avatarUrl: string
      quotaResetsAt: number
      isMinor: boolean
    }

export function WebRecorder(props: WebRecorderProps): JSX.Element {
  const navigate = useNavigate()

  const preloadedMoteConversationStateContext =
    usePreloadedMoteConversationStateContext()

  const [recordingInitiated, setRecordingInitiated] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [showTranscriptionLanguagePicker, setShowTranscriptionLanguagePicker] =
    useState(false)
  const [transcriptionLanguage, setTranscriptionLanguage] =
    useTranscriptionLocale()
  const viewportWidth = useViewportWidth()
  const isSmall = Boolean(viewportWidth && viewportWidth <= 640)

  const moteRecorder = useMoteRecorder({
    manualStatusHandling: true,
    getIsLoggedIn: () => true,
    hasOptedOutOfTranscription: () => false,
    getTranscriptionLanguage: () => transcriptionLanguage.code,
    getRegisterExtraPayload: () => ({
      transcriptionLanguage: transcriptionLanguage.code,
      isAnonymous: props.isAnonymous
    }),
    getExtraTrackingPayload: () => ({
      //@ts-expect-error
      onFinalize: {
        webRecorder: 1,
        application: 'Web Recorder'
      }
    }),
    //@ts-expect-error
    onFinalizeRecording: data => {
      preloadedMoteConversationStateContext.set(data)
      navigate(`/m/${data.moteStatus.moteId}`)
    },
    onCancelRecording: () => {
      setRecordingInitiated(false)
    },
    onError: () => {
      setRecordingInitiated(false)
    },
    onReset: () => {
      setRecordingInitiated(false)
    }
  })

  const animation = useWebRecorderAnimation(
    recordingInitiated,
    moteRecorder.status.raw as string
  )

  const avatar = (
    <AnimatedWrapper>
      <RecorderAvatar {...props} />
    </AnimatedWrapper>
  )

  if (isFinalizing) {
    return <ThinkingState>{avatar}</ThinkingState>
  }

  const trailItems = [
    avatar,
    <AnimatedWrapper>
      <RecordingTime
        fontSize='xl'
        fontWeight='bold'
        lineHeight='xl'
        seconds={moteRecorder.seconds}
      />
    </AnimatedWrapper>
  ]

  return (
    <>
      <Container
        minHeight='100%'
        minWidth={0}
        position='relative'
        w='full'
        variant={isSmall ? 'narrow' : undefined}
      >
        {animation.wrapperTransition((style, item) => (
          <>
            {item && props.isAnonymous ? (
              <AnimatedFeaturesWrapper
                style={{
                  opacity: style.opacity.to({
                    range: [0.0, 1.0],
                    output: [0, 1]
                  })
                }}
              >
                <WebRecorderFeaturesBanner />
              </AnimatedFeaturesWrapper>
            ) : null}
            {item ? (
              <StyledIdleContentWrapper
                style={{
                  opacity: style.opacity.to({
                    range: [0.0, 1.0],
                    output: [0, 1]
                  })
                }}
              >
                <StyledCTAContentWrapper>
                  <AnimatedWrapper style={animation.recordButtonStyles}>
                    <RecordButton
                      isAnimated
                      dropShadow
                      onClick={async () => {
                        await moteRecorder.startRecording()
                        setRecordingInitiated(true)
                      }}
                    />
                  </AnimatedWrapper>
                  <StyledClickToRecordText>
                    Click ☝️ to record
                  </StyledClickToRecordText>
                </StyledCTAContentWrapper>
                <Container variant={isSmall ? 'narrow' : undefined}>
                  <StyledLanguagePickerWrapper isAnonymous={props.isAnonymous}>
                    <Text
                      fontWeight={400}
                      fontSize={16}
                      lineHeight={'150%'}
                      tag={'span'}
                      color={'grey.d2'}
                    >
                      I'm speaking:
                    </Text>
                    <Button
                      // @ts-expect-error
                      text={`${transcriptionLanguage.name}`}
                      variant={'secondary'}
                      onClick={() => setShowTranscriptionLanguagePicker(true)}
                    />
                    {props.isAnonymous ? (
                      <StyledFeaturesWrapper>
                        <WebRecorderFeaturesBanner />
                      </StyledFeaturesWrapper>
                    ) : null}
                  </StyledLanguagePickerWrapper>
                </Container>
              </StyledIdleContentWrapper>
            ) : (
              <StyledRecordingContentWrapper
                style={{
                  opacity: style.opacity.to({
                    range: [1.0, 0.0],
                    output: [1, 0]
                  })
                }}
              >
                <StyledRecordingMainContentWrapper>
                  <>
                    {animation.mainContentTransition.map((style, index) => {
                      const item = trailItems[index]
                      return React.cloneElement(item, {
                        ...item.props,
                        key: index,
                        style
                      })
                    })}
                    {moteRecorder.status.raw === 'paused' && (
                      <StyledPausedText>Paused</StyledPausedText>
                    )}
                  </>
                </StyledRecordingMainContentWrapper>
                <StyledRecorderControlsWrapper
                  style={animation.recordingControlsStyles}
                >
                  <StyledScrubberWrapper>
                    <MeasuredAudioWaveformRecorder
                      recording={moteRecorder.status.raw === 'recording'}
                      stream={moteRecorder.audioStream}
                      audioContext={moteRecorder.audioContext}
                    />
                  </StyledScrubberWrapper>
                  <StyledRecorderButtonsWrapper>
                    <RecorderButtonRow
                      onCancel={() => {
                        moteRecorder.cancelRecording()
                      }}
                      onComplete={async () => {
                        setIsFinalizing(true)
                        await moteRecorder.stopRecording()
                      }}
                      state={
                        moteRecorder.status.raw === 'recording'
                          ? 'playing'
                          : 'paused'
                      }
                      onResume={async () => {
                        await moteRecorder.resumeRecording()
                      }}
                      onPause={async () => {
                        await moteRecorder.pauseRecording()
                      }}
                    />
                  </StyledRecorderButtonsWrapper>
                </StyledRecorderControlsWrapper>
              </StyledRecordingContentWrapper>
            )}
          </>
        ))}
      </Container>
      <TranscriptTranslationPicker
        variant={'transcription'}
        isOpen={showTranscriptionLanguagePicker}
        value={transcriptionLanguage.code}
        onChange={selectedLanguage => {
          setTranscriptionLanguage(selectedLanguage.code)
          setShowTranscriptionLanguagePicker(false)
        }}
        onClose={() => setShowTranscriptionLanguagePicker(false)}
      />
      {moteRecorder.error && !props.isAnonymous ? (
        <RecordingError
          type={moteRecorder.error}
          onClose={moteRecorder.reset}
          isMinor={props.isMinor}
          nextFreeBatchDate={props.quotaResetsAt}
        />
      ) : null}
    </>
  )
}

interface ThinkingStateProps {
  children: JSX.Element
}

function ThinkingState(props: ThinkingStateProps) {
  const mainContentTransition = useTrail(2, {
    config: { mass: 5, tension: 2000, friction: 200 },
    from: { opacity: 0, scale: 0 },
    to: { opacity: 1, scale: 1 }
  })

  const subItems = getChildren(
    [
      props.children,
      <AnimatedWrapper>
        <StyledClickToRecordText>Just a motement...</StyledClickToRecordText>
      </AnimatedWrapper>
    ],
    mainContentTransition
  )

  const mainItems = [
    <AnimatedStyledMainContentWrapper>
      {subItems}
    </AnimatedStyledMainContentWrapper>,
    <AnimatedStyledMainContentWrapper>
      <Spinner radius={42} />
    </AnimatedStyledMainContentWrapper>
  ]

  function getChildren<
    T extends JSX.Element[],
    S extends Record<string, SpringValue>[]
  >(items: T, styles: S) {
    return items.map((item, index) => {
      return React.cloneElement(item, {
        ...item.props,
        key: index,
        style: styles[index]
      })
    })
  }

  return (
    <StyledThinkingContentWrapper>
      {getChildren(mainItems, mainContentTransition)}
    </StyledThinkingContentWrapper>
  )
}
